import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { CareEnvironment, careEnvironmentList } from '@shared';
import { selectClient, selectClientLocalCareEnvironmentCosts } from '@client/model';

export function useCareEnvironmentCosts(
  updatePreviewClient: (updatedClient: Client) => void,
  previewClient: Client,
) {
  const client = useSelector(selectClient);
  const localCosts = useSelector(selectClientLocalCareEnvironmentCosts);

  const previewClientRef = useRef(previewClient);
  previewClientRef.current = previewClient;

  const [costs, setCosts] = useState<CareEnvironmentCosts>(client.careEnvironmentCosts ?? {});

  const initUseLocalRate = () => {
    const result: Partial<Record<CareEnvironment, boolean>> = {};
    careEnvironmentList.forEach(envDef => {
      const currentValue = costs[envDef.dbId as CareEnvironment];
      result[envDef.dbId as CareEnvironment] =
        currentValue === null || currentValue === undefined;
    });
    return result;
  };

  const [useLocalRate, setUseLocalRate] =
    useState<Partial<Record<CareEnvironment, boolean>>>(initUseLocalRate);

  const saveCost = useCallback(
    (env: CareEnvironment, cost: number | null, useLocal: boolean) => {
      setUseLocalRate(prev => ({ ...prev, [env]: useLocal }));

      setCosts(prev => {
        const updated = { ...prev, [env]: cost };

        const updatedClient = {
          ...previewClientRef.current,
          careEnvironmentCosts: updated,
        };
        updatePreviewClient(updatedClient);

        return updated;
      });
    },
    [updatePreviewClient],
  );

  const getResetCostPatches = useCallback(() => {
    const resetValues: CareEnvironmentCosts = {};
    setCosts(resetValues);

    const resetLocal: Partial<Record<CareEnvironment, boolean>> = {};
    careEnvironmentList.forEach(envDef => {
      resetLocal[envDef.dbId as CareEnvironment] = true;
    });
    setUseLocalRate(resetLocal);
    return { careEnvironmentCosts: resetValues };
  }, []);

  const resetCosts = useCallback(() => {
    const patches = getResetCostPatches();
    updatePreviewClient({ ...previewClientRef.current, ...patches });
  }, [getResetCostPatches, updatePreviewClient]);

  const isAllDefaultCosts = useCallback(() => {
    return Object.keys(costs).length === 0 || Object.values(costs).every(v => v == null);
  }, [costs]);

  return {
    costs,
    useLocalRate,
    localCosts,
    saveCost,
    resetCosts,
    getResetCostPatches,
    isAllDefaultCosts,
  };
}
